<template>
    <div>
        <Breadcrumb v-show="breadcrumbItems.length" :model="breadcrumbItems" class="sticky-header" />
        <Tree :value="treeData" :expandedKeys="expandedKeys" :icons="false" :filter="true" filterMode="lenient"
            filterBy="filterText" class="w-full md:w-[30rem]">
            <template #default="{ node }">
                <div class="p-treenode-content" @click="toggleNode(node)" :data-label="node.label"
                    :data-path="getNodePath(node).join('/')" :data-label-path="JSON.stringify(getNodePath(node))">
                    <span v-if="node.children" class="tree-toggler p-link" @click.stop="toggleNode(node)">
                        <i :class="getTogglerIcon(node)"></i>
                    </span>
                    <div class="json-line" :style="!node.meta ? 'padding-left: 2.3rem' : ''">
                        <span class="key">{{ node.label }}</span>
                        <span v-if="node.meta" class="meta">: {{ node.meta }}</span>
                        <template v-if="node.leaf">
                            <span class="colon">: </span>
                            <span :class="'type-' + getType(node.data)">
                                {{ formatValue(node.data) }}
                                <ag-copy-paste v-if="node.key == '_id'" :text="node.data"></ag-copy-paste>
                            </span>
                        </template>
                    </div>
                </div>
            </template>
        </Tree>
    </div>
</template>

<script>
import { Tree } from 'primevue/tree';
import AgCopyPaste from "@/components/ag-copy-paste/index.vue";
import Breadcrumb from 'primevue/breadcrumb';

export default {
    name: 'PrettyView',
    components: {
        Tree,
        AgCopyPaste,
        Breadcrumb
    },
    computed: {
        breadcrumbItems() {
            return this.breadcrumb
                .slice(0, -1)
                .map(label => ({ label }));

        }
    },
    props: {
        data: {
            type: Object,
            required: true
        },
        open: { type: Boolean, default: true }
    },
    data() {
        return {
            treeData: [],
            expandedKeys: {},
            breadcrumb: []
        };
    },
    watch: {
        data: {
            immediate: true,
            handler() {
                this.generateTree();
            }
        },
        open: {
            immediate: true,
            handler() {
                this.generateTree();
            }
        }
    },
    beforeUnmount() {
        const container = this.$el.querySelector('.p-tree');
        if (container) {
            container.removeEventListener('scroll', this.handleScroll);
        }
    },
    mounted() {
        this.$nextTick(() => {
            const container = this.$el.querySelector('.p-tree');
            if (container) {
                container.addEventListener('scroll', this.handleScroll);
                this.handleScroll();
            }
        });
    },

    methods: {
        generateTree() {
            const cleanedData = JSON.parse(JSON.stringify(this.data));

            const keysToRemove = ['Timestamps', 'GroupGuid', 'LastSeen', 'PolicyDownloadDate', 'Occurrences', 'SeverityRuleId', 'EventIpV4', 'EventIpV6', 'uniqueEventHash', 'eventCount', 'facilityName', 'typeName', 'CreatedForXml', 'LastAccessedForXml', 'ModifiedForXml'];
            const deepClean = (obj) => {
                if (Array.isArray(obj)) {
                    return obj.map(deepClean);
                } else if (typeof obj === 'object' && obj !== null) {
                    keysToRemove.forEach(key => delete obj[key]);
                    Object.entries(obj).forEach(([k, v]) => {
                        obj[k] = deepClean(v);
                    });
                }
                return obj;
            }

            deepClean(cleanedData);

            const labelMapping = {
                'Process': 'Actor File / Process',
                'ProcessActor': 'Actor File / Process',
                'ProcessTarget': 'Target File / Process',
                'RegistryKey': 'Target Registry Key',
                '_id': 'Event Id',
                '_t': 'Event Type'
            };
            const convert = (value, key = 'root', parent = null) => {
                const displayLabel = labelMapping[key] || key;
                if (Array.isArray(value)) {
                    const children = value.map((v, i) => convert(v, `[${i}]`));
                    const node = {
                        key,
                        label: displayLabel,
                        meta: `${children.length} ${children.length <= 1 ? 'element' : 'elements'}`,
                        children,
                        parent,
                        filterText: `${displayLabel} ${['string', 'number', 'boolean'].includes(typeof value) ? value : ''}`
                    };
                    node.children.forEach(child => {
                        child.parent = node;
                    });
                    return node;
                } else if (typeof value === 'object' && value !== null) {
                    let entries = Object.entries(value);

                    if (key === 'EventDetails') {
                        const isMemoryGuardEvent = value._t === 'MemoryGuardEvent';
                        if (!isMemoryGuardEvent) {

                            const registryKeyEntry = entries.find(([k]) => k === 'RegistryKey');
                            const processEntry = entries.find(([k]) => k === 'Process');
                            const fileEntry = entries.find(([k]) => k === 'File');
                            const ruleEntry = entries.find(([k]) => k === 'Rule');

                            const others = entries.filter(
                                ([k]) => !['Process', 'File', 'Rule', 'RegistryKey'].includes(k)
                            );

                            entries = [];
                            if (processEntry) entries.push(processEntry);
                            if (registryKeyEntry) entries.push(registryKeyEntry);
                            if (fileEntry) entries.push(['Target File / Process', fileEntry[1]]);
                            if (ruleEntry) entries.push(ruleEntry);
                            entries.push(...others);
                        } else {
                            const ProcessActorEntry = entries.find(([k]) => k === 'ProcessActor');
                            const ProcessTargetEntry = entries.find(([k]) => k === 'ProcessTarget');
                            const memoryRuleEntry = entries.find(([k]) => k === 'Rule');

                            const others = entries.filter(
                                ([k]) => !['ProcessActor', 'ProcessTarget', 'Rule'].includes(k)
                            );

                            entries = [];

                            if (ProcessActorEntry) entries.push(ProcessActorEntry);
                            if (ProcessTargetEntry) entries.push(ProcessTargetEntry);
                            if (memoryRuleEntry) entries.push(memoryRuleEntry);
                            entries.push(...others);
                        }
                    }
                    const children = entries.map(([k, v]) => convert(v, k));
                    const node = {
                        key,
                        label: displayLabel,
                        meta: `${children.length} ${children.length === 1 ? 'property' : 'properties'}`,
                        children,
                        parent,
                        filterText: `${displayLabel} ${['string', 'number', 'boolean'].includes(typeof value) ? value : ''}`
                    };
                    node.children.forEach(child => {
                        child.parent = node;
                    });
                    return node;
                } else {
                    let displayValue = value;
                    if ((key === 'ReportedEventDate' || key === 'LastAccessed' || key === 'Modified' || key === 'Created' || key === 'First' || key === 'Last') && (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value))) {
                        const date = new Date(value);
                        const utcOffset = 3 * 60;
                        const localDate = new Date(date.getTime() + utcOffset * 60 * 1000);
                        const options = {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: true,
                            timeZone: 'UTC'
                        };
                        displayValue = new Intl.DateTimeFormat('en-US', options).format(localDate).replace(',', '');
                    }
                    return {
                        key,
                        label: displayLabel,
                        data: displayValue,
                        leaf: true,
                        parent,
                        filterText: `${displayLabel} ${['string', 'number', 'boolean'].includes(typeof value) ? value : ''}`
                    };
                }
            };

            const root = convert(cleanedData);
            this.treeData = [root];

            this.expandedKeys = {};
            if (this.open) {
                this.expandAll(root);
            }
        },
        expandAll(node) {
            this.expandedKeys[node.key] = true;
            if (node.children) {
                node.children.forEach(this.expandAll);
            }
        },
        getType(value) {
            if (value === null) return 'null';
            return typeof value;
        },
        formatValue(value) {
            if (value === null) return 'null';
            if (typeof value === 'string') return `"${value}"`;
            return value;
        },
        toggleNode(node) {
            if (!node.children) return;

            if (this.expandedKeys[node.key]) {
                delete this.expandedKeys[node.key];
            } else {
                this.expandedKeys[node.key] = true;
            }
        },
        getTogglerIcon(node) {
            return this.expandedKeys[node.key]
                ? 'pi pi-chevron-down'
                : 'pi pi-chevron-right';
        },
        getNodePath(node) {
            const path = [];
            let current = node;
            while (current) {
                path.unshift(current.label);
                current = current.parent;
            }
            return path;
        },
        handleScroll() {
            const container = this.$el.querySelector('.p-tree');
            if (!container) return;

            const treeNodes = container.querySelectorAll('.p-treenode-content');

            let topNode = null;
            let minDistance = Infinity;

            treeNodes.forEach((el) => {
                const rect = el.getBoundingClientRect();
                const distance = Math.abs(rect.top - container.getBoundingClientRect().top);

                if (rect.top >= container.getBoundingClientRect().top && distance < minDistance) {
                    minDistance = distance;
                    topNode = el;
                }
            });

            if (topNode) {
                const path = topNode.dataset.path;
                if (path) {
                    this.breadcrumb = JSON.parse(topNode.dataset.labelPath || '[]');
                }
            }
        },
    }
};
</script>
<style lang="scss" scoped>
.p-tree {
    font-size: 16px;
    max-height: 600px;
    overflow-y: auto;
    line-height: 1.4;
}

::v-deep(.p-treenode-label) {
    width: 100%;
}

::v-deep(.p-tree-toggler) {
    display: none !important;
}


.tree-toggler {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    margin-right: 0.5rem;
    border-radius: 4px;
    transition: background-color 0.2s ease;
    color: #334155;
    cursor: pointer;

    &:hover {
        color: #e2e8f0;
    }

    i {
        font-size: 1rem;
    }
}

.key {
    color: #1e40af;
    font-weight: 600;
}

.colon {
    color: #aaa;
}

.type-string {
    color: #15803d;
}

.type-number {
    color: #0e7490;
}

.type-boolean {
    color: #c2410c;
}

.type-null {
    color: #6b21a8;
}

.json-line {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    padding: 2px 8px;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s;
}

.json-line:hover {
    background-color: #f1f5f9;
}

.meta {
    color: #999;
    margin-left: 4px;
    font-weight: normal;
    font-size: 14px;
}

.sticky-header {
    position: sticky;
    top: 0;
    background: white;
    padding: 0.75rem 1rem;
    font-weight: 500;
    border-bottom: 1px solid #e0e0e0;
    z-index: 10;
}

::v-deep(.p-breadcrumb) {
    background: transparent !important;
    border: none !important;
    padding: 0;
    font-size: 14px;
}

::v-deep(.p-breadcrumb .p-menuitem-text) {
    color: #6c757d;
}

::v-deep(.p-breadcrumb .pi) {
    color: #6c757d;
}
</style>